import { Card } from '@mui/material';

import {
  type CareSearchInitial,
  ProviderCardContent,
  StatusChipMenu,
} from '@/cam-admin/care-search/CareSearch';
import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { careSearchSceneById } from '@/cam-admin/care-search/CareSearch.scenes';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

// One live control panel mini per flow step / state, opened on the care
// search tab and jumped straight to its scenario via the initial prop. The
// initial for each scene comes from the shared catalog (CareSearch.scenes.ts),
// so the live state select and this handover can't drift apart.
const screen = (initial?: CareSearchInitial) => (
  <ControlPanel embedded initialTab='care-search' careSearch={initial} />
);

// Render the prototype in a named catalog scene.
const scene = (id: string) => screen(careSearchSceneById(id).initial);

// The CAM-side handover for embedded provider search and shortlist management
// (mini-PRD V1), rendered live from the CareSearch prototype. The member-side
// counterpart is care-shortlists.tsx.
export const camCareSearchSpec: HandoverSpec = {
  id: 'cam-care-search',
  title: 'CAM care search & shortlists',
  screen: 'desktop',
  description:
    'Provider search embedded in the care recipient’s control panel. CAMs search and filter providers, build named shortlists, track a status per provider, write notes for the family, and publish when ready. No more switching to CareSourcer. The care circle only ever sees published shortlists.',
  links: [
    {
      label: 'Mini-PRD (Linear)',
      url: 'https://linear.app/karehero/document/mini-prd-care-provider-search-in-the-control-panel-3d45177cd945',
    },
    {
      label: 'User stories (Linear)',
      url: 'https://linear.app/karehero/document/user-stories-cam-admin-shortlist-and-provider-search-de0cca486b50',
    },
    { label: 'Live prototype (Care search)', url: '/care-search' },
    {
      label: 'Member-side handover (care shortlists)',
      url: '/handover/care-shortlists',
    },
  ],
  flows: [
    {
      title: 'Search to published shortlist',
      steps: [
        {
          id: 'first-visit',
          title: 'First visit',
          tap: 'Control panel › Care search tab',
          render: () => scene('first-visit'),
        },
        {
          id: 'results',
          title: 'Search results',
          tap: 'Search by name or location',
          render: () => scene('search-results'),
        },
        {
          id: 'create',
          title: 'Create shortlist',
          tap: 'Tap ‘Create shortlist’',
          render: () => scene('create'),
        },
        {
          id: 'draft',
          title: 'Draft shortlist',
          tap: 'Add providers from the results',
          render: () => scene('draft'),
        },
        {
          id: 'detail',
          title: 'Provider detail',
          tap: 'Click a provider card',
          render: () => scene('provider-detail'),
        },
        {
          id: 'dismiss',
          title: 'Dismiss a provider',
          tap: 'Card menu › Dismiss from shortlist',
          render: () => scene('dismiss'),
        },
        {
          id: 'publish',
          title: 'Publish',
          tap: 'Tap ‘Publish’',
          render: () => scene('publish'),
        },
        {
          id: 'published',
          title: 'Published',
          tap: 'Confirm publish',
          render: () => scene('published'),
        },
      ],
    },
  ],
  states: [
    {
      id: 'draft-in-progress',
      title: 'Draft shortlist in progress',
      kind: 'happy',
      repro: 'Scenario: Draft shortlist. Statuses and notes on seed providers',
      render: () => scene('draft'),
    },
    {
      id: 'published-live',
      title: 'Published shortlist',
      kind: 'happy',
      repro: 'Scenario: Published shortlist. Edits stay live once published',
      render: () => scene('published'),
    },
    {
      id: 'detail-notes',
      title: 'Provider detail with global notes',
      kind: 'happy',
      repro:
        'Click any provider card. Notes are CAM-only, never shown to the circle',
      render: () => scene('provider-detail'),
    },
    {
      id: 'map-view',
      title: 'Map view',
      kind: 'happy',
      repro: 'Map toggle on search results. Mock map, pins are illustrative',
      render: () => scene('map-view'),
    },
    {
      id: 'notes-dialog',
      title: 'Global notes from a result card',
      kind: 'happy',
      repro: '‘Add note to provider’ on any result card',
      render: () => scene('notes'),
    },
    {
      id: 'first-visit-empty',
      title: 'Nothing yet',
      kind: 'empty',
      repro: 'Scenario: First visit. No search run, no shortlists',
      render: () => scene('first-visit'),
    },
    {
      id: 'searched-no-list',
      title: 'Searched, no shortlist',
      kind: 'empty',
      repro: 'Scenario: Searched, no shortlist. Empty pane prompts create',
      render: () => scene('search-results'),
    },
    {
      id: 'republish',
      title: 'Re-publish',
      kind: 'edge',
      repro: 'Publish on an already-published list notifies the circle again',
      render: () => scene('republish'),
    },
    {
      id: 'detail-waitlist',
      title: 'Waitlist provider',
      kind: 'edge',
      repro:
        'Cherry Tree house: availability shows Waitlist on card and detail',
      render: () => scene('detail-waitlist'),
    },
    {
      id: 'dismissed-visible',
      title: 'Dismissed providers',
      kind: 'edge',
      repro:
        'Show dismissed toggle. Hillcrest manor seed: red badge, reason, restore',
      render: () => scene('dismissed'),
    },
  ],
  requirements: [
    {
      text: 'Provider search embedded in the care recipient’s control panel page',
      status: 'built',
      where: 'Care search tab',
    },
    {
      text: 'Search pre-fills location and care type from the profile, both overridable',
      status: 'partial',
      note: 'Filters arrive pre-seeded in the mock. No profile wiring',
    },
    {
      text: 'Full filter parity with CareSourcer: location, name, care type, distance, care provided, rating, hourly rate, visit duration, sort',
      status: 'partial',
      where: 'Search results',
      note: 'Sort control built. Filter options are still fixed and don’t vary by care type or regulator',
    },
    {
      text: 'Result cards: cover photo, name, address, care type chips, availability, rating, distance, price, phone',
      status: 'built',
      where: 'Search results',
      note: 'Cover photo is a placeholder block',
    },
    {
      text: 'Add to shortlist from a result card',
      status: 'built',
      where: 'Split button on every card',
    },
    {
      text: 'Add a global note from a result card',
      status: 'built',
      where: '‘Add note to provider’ on result cards',
    },
    {
      text: 'Provider detail with full CareSourcer parity',
      status: 'built',
      where: 'Provider detail',
      note: 'Stats, contact, care types, features, owner quote, pricing and funding, inspection breakdown. Photos are placeholder blocks',
    },
    {
      text: 'Add to shortlist from the detail view',
      status: 'built',
      where: 'Provider detail',
    },
    {
      text: 'Global provider notes: CAM-only, editable, visible wherever the provider appears',
      status: 'built',
      where: 'Result cards and provider detail',
      note: 'One shared feed per provider. Add, edit and delete your own notes',
    },
    {
      text: 'Named shortlists, several per care recipient, default name when naming is skipped',
      status: 'built',
      where: 'Create shortlist modal',
    },
    {
      text: 'Delete or archive a shortlist',
      status: 'built',
      where: 'Delete in the shortlist pane header',
      note: 'PRD says delete, the user stories say archive. Prototype deletes with a confirm',
    },
    {
      text: 'Draft and publish workflow. Edits to a published list go live immediately',
      status: 'built',
      where: 'Publish and re-publish dialogs',
    },
    {
      text: 'Dismiss a provider with a reason: red badge, bumped to the bottom, show-dismissed toggle',
      status: 'built',
      where: 'Card menu › Dismiss from shortlist',
      note: 'Reason is a category plus optional detail. Format is still an open PRD question',
    },
    {
      text: 'Per-provider status: Not contacted / Contacted / Secured / Unavailable, changeable any time',
      status: 'built',
      where: 'Status chip menu on shortlist cards',
    },
    {
      text: 'Status changes timestamped for the audit trail',
      status: 'built',
      where: 'Date on the status chip',
    },
    {
      text: 'One note per provider per shortlist, visible to the circle after publish',
      status: 'built',
      where: 'Add note on shortlist cards',
    },
    {
      text: 'Notify the care circle and create a ‘Review your shortlist’ action on publish',
      status: 'partial',
      note: 'Publish dialog copy promises it. Nothing fires in the prototype',
    },
    {
      text: 'Map view of search results',
      status: 'built',
      where: 'Map toggle on search results',
      note: 'Stretch. Mock map, pin positions are illustrative',
    },
    {
      text: 'Bulk add, suggest similar, global note audit trail',
      status: 'out-of-scope',
      note: 'Stretch in the PRD',
    },
    {
      text: 'Threaded notes, provider admin editing, Twilio click-to-call',
      status: 'out-of-scope',
      note: 'Later per the PRD',
    },
  ],
  components: {
    mui: [
      'Button',
      'ButtonGroup',
      'Chip',
      'Dialog',
      'Menu',
      'Snackbar',
      'Tabs',
      'TextField (select)',
      'ToggleButtonGroup',
    ],
    existing: ['Icon (llama)', 'Card (MUI)'],
    new: [
      'ProviderCardContent',
      'StatusChipMenu',
      'AddToShortlistSplitButton',
      'AddToShortlistMenuButton',
      'ShortlistPane',
      'ShortlistCard',
      'ShortlistModal',
      'DismissDialog',
      'DismissedCard',
      'MapView',
      'NotesPanel',
      'ProviderNotesDialog',
      'CareDetail',
      'NoteItem',
    ],
  },
  componentsNote:
    "The card primitive is the themed MUI Card: `variant='padded'` for a plain block, Card + CardActionArea for a clickable one. There used to be a Surface wrapper in components/wrapped doing exactly that and nothing else, so it went. The rest are local to src/pages/CareSearch.tsx in the designs app. ProviderCardContent is content-only and is composed into a Card by search results and shortlist items, so the typography stays consistent with one card primitive.",
  componentPreviews: {
    Card: () => (
      <Card variant='padded'>
        Any block of content, on the one card primitive.
      </Card>
    ),
    ProviderCardContent: () => (
      <Card variant='padded'>
        <ProviderCardContent
          name='Oakwood care home'
          address='14 Brae Street, Edinburgh EH3 6HR'
          distance='0.8 miles'
          phone='0131 555 0299'
        />
      </Card>
    ),
    StatusChipMenu: () => (
      <StatusChipMenu status='Contacted' onStatus={noop} onDismiss={noop} />
    ),
  },
};
